import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Center,
  Checkbox,
  HStack,
  Heading,
  Image,
  Pressable,
  Spinner,
  Text,
  VStack,
  useDisclose,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { FiChevronLeft, FiLock } from "react-icons/fi";
import { io } from "socket.io-client";
import { AiOutlineCreditCard } from "react-icons/ai";
import { BiTransfer } from "react-icons/bi";
import { MdAdd, MdClose, MdOutlineAccountBalanceWallet } from "react-icons/md";
import colors from "../../../constants/colors";
import API, { baseURL } from "../../../api";
import { StateContext, StatusContext } from "../../../contexts";
import routes from "../../../routes";
import calendar from "../../../assets/icons/calendar.svg";
import people from "../../../assets/icons/people.svg";
import clock from "../../../assets/icons/clock.svg";
import { capitalize, formatToCurrency } from "../../../utils";
import PendingPaymentModal from "../../../components/pendingPaymentModal";
import FilePicker from "../../../components/filePicker";

function TourPurchaseSummaryPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const sku = useMemo(() => searchParams.get("sku"), [searchParams]);
  const status = useContext(StatusContext);
  const state = useContext(StateContext);
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [tour, setTour] = useState({});
  const [type, setType] = useState("");
  const [isPaying, setIsPaying] = useState(false);
  const {
    isOpen: isPendingPaymentModalOpen,
    onOpen: onOpenPendingPaymentModal,
    onClose: onClosePendingPaymentModal,
  } = useDisclose();
  const [file, setFile] = useState(null);

  const getCart = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: { data },
      } = await API(state.jwt).get(`/carts/${sku}`);
      setItems(data.items);
      setTour(data.service.tour);
      setIsLoading(false);
    } catch (error) {
      status.updateErrorMessage(error);
      navigate(routes.tour.replace(":id", params.id));
    }
  }, [navigate, params.id, sku, state.jwt, status]);

  useEffect(() => {
    getCart();
  }, [getCart]);

  const origin = useMemo(
    () =>
      tour?.itinerary?.find(
        (location) => !location.isReturn && location.type === "origin",
      ),
    [tour],
  );

  const total = useMemo(
    () => items.reduce((accumulator, item) => accumulator + item.cost, 0),
    [items],
  );

  const onPay = useCallback(async () => {
    try {
      const files = [];
      if (type === "transfer" && !file) {
        status.updateWarningMessage("Por favor adjunta el comprobante");
        return;
      }
      setIsPaying(true);
      if (type === "transfer") {
        const formData = new FormData();
        formData.append("files", file);
        const {
          data: [{ id }],
        } = await API(state.jwt).post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        files.push(id);
      }
      const { data } = await API(state.jwt).post(`/carts/${sku}/payment`, {
        data: { type, files },
      });
      if (type === "card") {
        window.open(data.url, "_blank", "noopener,noreferrer");
        const socket = io(baseURL);
        await new Promise((resolve) => socket.on("connect", resolve));
        onOpenPendingPaymentModal();
        socket.on(`payment:${data.id}`, (payment) => {
          socket.close();
          if (payment.status === "approved") {
            onClosePendingPaymentModal();
            navigate(routes.tickets);
          } else {
            onClosePendingPaymentModal();
            status.updateWarningMessage("Pago rechazado, intente nuevamente");
          }
        });
      } else if (type === "cash" || type === "transfer") {
        navigate(routes.tickets);
      }
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsPaying(false);
  }, [
    type,
    file,
    state.jwt,
    sku,
    status,
    onOpenPendingPaymentModal,
    onClosePendingPaymentModal,
    navigate,
  ]);

  const includesPaymentType = useCallback(
    (paymentType) =>
      tour?.paymentTypes
        ?.map((paymentType) => paymentType.type)
        .includes(paymentType),
    [tour],
  );

  if (isLoading) {
    return (
      <Center height="70vh">
        <Spinner size="lg" color={colors.primary} />
      </Center>
    );
  }

  return (
    <VStack>
      <Helmet>
        <title>Resumen de compra</title>
      </Helmet>
      <VStack paddingX={10} marginTop={10} space={10}>
        <HStack space={20} alignItems="flex-start">
          <VStack space={8}>
            <Heading color={colors.secondary} fontSize="xl">
              Finalizar compra
            </Heading>
            <VStack
              paddingX={8}
              paddingY={4}
              borderColor={colors.secondary}
              borderWidth={1}
              borderRadius={10}
              width={400}
              space={5}>
              <Heading color={colors.secondary} fontSize="2xl">
                {tour?.name}
              </Heading>
              <VStack>
                <HStack space={2}>
                  <Image
                    src={calendar}
                    alt="calendar"
                    width={4}
                    height={4}
                    tintColor={colors.secondary}
                  />
                  <Text color={colors.secondary}>
                    {capitalize(
                      moment(origin?.departureDate).format("ddd, DD [de] MMM"),
                    )}
                  </Text>
                </HStack>
                <HStack space={2}>
                  <Image
                    src={people}
                    alt="people"
                    width={4}
                    height={4}
                    tintColor={colors.secondary}
                  />
                  <Text color={colors.secondary}>
                    {items.length} persona{items.length !== 1 && "s"}
                  </Text>
                </HStack>
                <HStack space={2}>
                  <Image
                    src={clock}
                    alt="clock"
                    width={4}
                    height={4}
                    tintColor={colors.secondary}
                  />
                  <Text color={colors.secondary}>
                    {moment(origin?.departureDate).format("HH:mm a")}
                  </Text>
                </HStack>
              </VStack>
              <VStack>
                {items
                  .filter(
                    (item, index, self) =>
                      index ===
                      self.findIndex((_item) => _item.name === item.name),
                  )
                  .map((item) => ({
                    ...item,
                    data: items.filter((_item) => _item.name === item.name),
                  }))
                  .map((item) => (
                    <Text key={item.id} color={colors.gray}>
                      {item.data.length} {item.name} x{" "}
                      {formatToCurrency(item.cost)}
                    </Text>
                  ))}
                <Text color={colors.gray}>
                  Subtotal: {formatToCurrency(total / 1.16)}
                </Text>
                <Text color={colors.gray}>
                  IVA: {formatToCurrency(total * (1 - 1 / 1.16))}
                </Text>
                <Text bold>Total: {formatToCurrency(total)}</Text>
              </VStack>
            </VStack>
            <VStack>
              <Checkbox
                defaultIsChecked
                colorScheme="danger"
                isDisabled={isPaying}>
                <Text color={colors.gray}>
                  Acepto los Terminos y Condiciones
                </Text>
              </Checkbox>
              <Checkbox
                defaultIsChecked
                colorScheme="danger"
                isDisabled={isPaying}>
                <Text color={colors.gray}>Acepto el Aviso de Privacidad</Text>
              </Checkbox>
            </VStack>
          </VStack>
          <VStack flex={1} space={5}>
            <HStack space={5}>
              <Heading color={colors.secondary} fontSize="2xl">
                Tramitar pago
              </Heading>
              <HStack space={2}>
                <FiLock color={colors.gray} />
                <Text color={colors.gray}>Seguro</Text>
              </HStack>
            </HStack>
            <Heading color={colors.secondary} fontSize="xl">
              Elige un método de pago
            </Heading>
            <HStack space={3} alignItems="stretch">
              {includesPaymentType("card") && (
                <Pressable onPress={() => setType("card")} disabled={isPaying}>
                  <VStack
                    paddingY={4}
                    paddingX={5}
                    borderColor={
                      type === "card" ? colors.primary : colors.black
                    }
                    borderWidth={2}
                    borderRadius={5}
                    alignItems="center"
                    height="full"
                    minWidth={120}>
                    <AiOutlineCreditCard
                      color={type === "card" ? colors.primary : colors.black}
                      size={40}
                    />
                    <Text
                      bold
                      fontSize="sm"
                      color={type === "card" ? colors.primary : colors.black}>
                      Tarjeta de
                    </Text>
                    <Text
                      bold
                      fontSize="sm"
                      color={type === "card" ? colors.primary : colors.black}>
                      crédito/débito
                    </Text>
                  </VStack>
                </Pressable>
              )}
              {includesPaymentType("cash") && (
                <Pressable onPress={() => setType("cash")} disabled={isPaying}>
                  <VStack
                    paddingY={4}
                    paddingX={5}
                    borderColor={
                      type === "cash" ? colors.primary : colors.black
                    }
                    borderWidth={2}
                    borderRadius={5}
                    alignItems="center"
                    height="full"
                    minWidth={120}>
                    <MdOutlineAccountBalanceWallet
                      color={type === "cash" ? colors.primary : colors.black}
                      size={40}
                    />
                    <Text
                      bold
                      fontSize="sm"
                      color={type === "cash" ? colors.primary : colors.black}>
                      Efectivo
                    </Text>
                  </VStack>
                </Pressable>
              )}
              {includesPaymentType("transfer") && (
                <Pressable
                  onPress={() => setType("transfer")}
                  disabled={isPaying}>
                  <VStack
                    paddingY={4}
                    paddingX={5}
                    borderColor={
                      type === "transfer" ? colors.primary : colors.black
                    }
                    borderWidth={2}
                    borderRadius={5}
                    alignItems="center"
                    height="full"
                    minWidth={120}>
                    <BiTransfer
                      color={
                        type === "transfer" ? colors.primary : colors.black
                      }
                      size={40}
                    />
                    <Text
                      bold
                      fontSize="sm"
                      color={
                        type === "transfer" ? colors.primary : colors.black
                      }>
                      Transferencia
                    </Text>
                    <Text
                      bold
                      fontSize="sm"
                      color={
                        type === "transfer" ? colors.primary : colors.black
                      }>
                      bancaria
                    </Text>
                  </VStack>
                </Pressable>
              )}
            </HStack>
            <Heading color={colors.primary} fontSize="xl">
              {type === "card"
                ? "Pago con tarjeta de crédito/débito"
                : type === "cash"
                ? "Pago en Efectivo"
                : type === "transfer" && "Trasferencia/Deposito bancario"}
            </Heading>
            <Text>
              {type === "card"
                ? "Al elegir pago con tarjeta de credito o debito te estaremos direccionando a un portal externo donde podras ingresar tu información de una manera facil y segura, tus datos estan siempre protegidos."
                : type === "cash"
                ? "Al elegir pago en Efectivo tus asientos seleccionados quedaran desbloqueados, primero deberás liquidar el importe del viaje directamente en la sucursal participante de tu empresa, para ello tienes 24 horas para realizar la transacción de lo contrario tu reserva quedara cancelada. Una vez liquidada tu reserva la empresa te asignara los asientos que se encuentre disponibles al momento de tu pago."
                : type === "transfer" &&
                  "Al elegir esta forma de pago tus asientos seleccionados quedaran desbloqueados, primero deberas realizar la transferencia desde tu portal o depositar directamente en ventanilla el importe de tu viaje a la siguiente cuenta:"}
            </Text>
            {type === "cash" && (
              <VStack space={3}>
                <Heading color={colors.black} fontSize="xl">
                  Datos sucursal
                </Heading>
                <Text bold>Teléfono</Text>
                <Text>{tour.agency.phone}</Text>
                <Text bold>Dirección</Text>
                <Text>{tour.agency.address}</Text>
                <Text bold>Horario de atención</Text>
                <Text>Lunes a viernes, 09:00 am - 06:00 pm</Text>
              </VStack>
            )}
            {type === "transfer" && (
              <VStack space={3}>
                <Heading color={colors.black} fontSize="xl">
                  Datos de la cuenta
                </Heading>
                <Text bold>Nombre del beneficiario </Text>
                <Text>{tour.agency.bankAccount.holder}</Text>
                <Text bold>Número de tu cuenta</Text>
                <Text bold>Clave</Text>
                <Text>{tour.agency.bankAccount.clave}</Text>
                <Text bold>Interbancaria</Text>
                <Text>{tour.agency.bankAccount.interbancaria}</Text>
                <Text bold>Nombre del banco</Text>
                <Text>{tour.agency.bankAccount.bankName}</Text>
                <VStack>
                  <FilePicker
                    onSelect={([_file]) => setFile(_file)}
                    disabled={isPaying}>
                    <HStack>
                      <MdAdd color={colors.primary} size={30} />
                      <Text color={colors.primary}>
                        Adjunta tu comprobante aqui
                      </Text>
                    </HStack>
                  </FilePicker>
                  {file?.name && (
                    <HStack marginLeft={30} marginTop={1} space={2}>
                      <Text fontSize="sm">
                        Archivo seleccionado: {file?.name}
                      </Text>
                      <Pressable onPress={() => setFile(null)}>
                        <MdClose color={colors.primary} size={20} />
                      </Pressable>
                    </HStack>
                  )}
                </VStack>
                <Text color={colors.gray}>
                  El proceso demora entre 24 y 48 horas para ser validado por
                  uno de nuestros asesores
                </Text>
                <Text color={colors.gray}>
                  Una vez validado el importe, la empresa te asignara los
                  asientos que se encuentren disponibles al momento de tu pago.
                </Text>
              </VStack>
            )}
          </VStack>
        </HStack>
        <HStack space={2} justifyContent="center">
          <Pressable
            alignSelf="center"
            onPress={() => navigate(-1)}
            disabled={isPaying}>
            <HStack
              backgroundColor={colors.lighterGray}
              borderRadius="full"
              paddingY={2}
              paddingX={5}
              space={2}>
              <FiChevronLeft color={colors.gray} />
              <Text color={colors.gray}>Regresar</Text>
            </HStack>
          </Pressable>
          <Pressable alignSelf="center" onPress={onPay} disabled={isPaying}>
            <Box
              backgroundColor={colors.primary}
              borderRadius="full"
              paddingY={2}
              paddingX={10}>
              <Text color={colors.white}>Pagar {formatToCurrency(total)}</Text>
            </Box>
          </Pressable>
        </HStack>
      </VStack>
      <PendingPaymentModal isOpen={isPendingPaymentModalOpen} />
    </VStack>
  );
}

export default TourPurchaseSummaryPage;
